import { useCallback, useMemo, useState } from 'react';

import { type Connector, CONNECTOR_RUNNING_STATUSES } from '@/entities/connector';

import {
  ActivationModal,
  CreateConnectorModal,
  DeleteConnectorsModal,
  EditConnectorModal,
  useConnectorActions,
} from '@/features/connectors';

import styles from './ConnectorTableContent.module.css';
import { getConnectorTableColumns } from './lib/getConnectorTableColumns';
import type { ConnectorTableRow } from './model/types';
import { useConnectorFilters } from './model/useConnectorFilters';
import { useConnectorModals } from './model/useConnectorModals';
import { useConnectorSelection } from './model/useConnectorSelection';
import { useConnectorTable } from './model/useConnectorTable';
import { ConnectorTableGrid } from './ui/ConnectorTableGrid';
import { ConnectorTableToolbar } from './ui/ConnectorTableToolbar';

const filterIdsByActivation = (ids: string[], connectorMap: Map<string, Connector>, isActive: boolean) =>
  ids.filter((id) => connectorMap.get(id)?.isActive === isActive);

const ConnectorTableContent = () => {
  const [editingConnectorId, setEditingConnectorId] = useState<string | undefined>(undefined);

  const { selectedActivation, selectedStatus, isActiveParam, statusParam, setSelectedActivation, setSelectedStatus } =
    useConnectorFilters();

  const {
    connectors,
    connectorMap,
    dataSource,
    paginationConfig,
    scrollConfig,
    isLoading,
    sortBy,
    sortOrder,
    handleTableChange,
  } = useConnectorTable({
    isActive: isActiveParam,
    status: statusParam,
  });
  const { selectedCount, selectedRowKeys, rowSelection, clearSelection } = useConnectorSelection(connectors);

  const {
    isCreateModalOpen,
    openCreateModal,
    isEditModalOpen,
    isDeleteModalOpen,
    isActivationModalOpen,
    activationMode,
    openEditModal,
    openDeleteModal,
    openActivateModal,
    openDeactivateModal,
    closeModal,
  } = useConnectorModals();

  const { isDeletePending, handleDeleteConfirm, handleStatusChange } = useConnectorActions();

  const handleEdit = useCallback(
    (row: ConnectorTableRow) => {
      setEditingConnectorId(row.id);
      openEditModal();
    },
    [openEditModal]
  );

  const connectorTableColumns = useMemo(
    () => getConnectorTableColumns({ onEdit: handleEdit, sortBy, sortOrder }),
    [handleEdit, sortBy, sortOrder]
  );

  const editingConnector = useMemo(() => {
    if (!editingConnectorId) return undefined;

    return connectorMap.get(editingConnectorId);
  }, [connectorMap, editingConnectorId]);

  const handleEditModalClose = useCallback(() => {
    setEditingConnectorId(undefined);
    closeModal();
  }, [closeModal]);

  const handleDeleteModalConfirm = useCallback(async () => {
    await handleDeleteConfirm(selectedRowKeys);
    closeModal();
  }, [closeModal, handleDeleteConfirm, selectedRowKeys]);

  const handleActivateAll = useCallback(async () => {
    const inactiveIds = filterIdsByActivation(selectedRowKeys, connectorMap, false);

    if (inactiveIds.length > 0) {
      await handleStatusChange(inactiveIds, CONNECTOR_RUNNING_STATUSES.RUNNING);
    }

    clearSelection();
    closeModal();
  }, [clearSelection, closeModal, connectorMap, handleStatusChange, selectedRowKeys]);

  const handleDeactivateAll = useCallback(async () => {
    const activeIds = filterIdsByActivation(selectedRowKeys, connectorMap, true);

    if (activeIds.length > 0) {
      await handleStatusChange(activeIds, CONNECTOR_RUNNING_STATUSES.STOPPED);
    }

    clearSelection();
    closeModal();
  }, [clearSelection, closeModal, connectorMap, handleStatusChange, selectedRowKeys]);

  return (
    <div className={styles.connectorTableContent}>
      <ConnectorTableToolbar
        hasSelected={selectedCount > 0 ? true : false}
        selectedActivation={selectedActivation}
        selectedStatus={selectedStatus}
        onDelete={openDeleteModal}
        openCreateModal={openCreateModal}
        onActivate={openActivateModal}
        onDeactivate={openDeactivateModal}
        onActivationFilterChange={setSelectedActivation}
        onStatusFilterChange={setSelectedStatus}
      />

      <ConnectorTableGrid
        columns={connectorTableColumns}
        dataSource={dataSource}
        rowSelection={rowSelection}
        paginationConfig={paginationConfig}
        scrollConfig={scrollConfig}
        isLoading={isLoading}
        onTableChange={handleTableChange}
      />

      {editingConnector && (
        <EditConnectorModal open={isEditModalOpen} connector={editingConnector} onClose={handleEditModalClose} />
      )}

      <DeleteConnectorsModal
        open={isDeleteModalOpen}
        selectedCount={selectedCount}
        confirmLoading={isDeletePending}
        onConfirm={handleDeleteModalConfirm}
        onCancel={closeModal}
      />

      <ActivationModal
        open={isActivationModalOpen}
        mode={activationMode ?? 'activate'}
        selectedCount={selectedCount}
        onConfirm={activationMode === 'deactivate' ? handleDeactivateAll : handleActivateAll}
        onCancel={closeModal}
      />

      <CreateConnectorModal open={isCreateModalOpen} onClose={closeModal} />
    </div>
  );
};

export { ConnectorTableContent };
